<template>
  <div class="card card-body">
    <!-- DOWNLOAD SAMPLE -->
    <button class="btn btn-outline-secondary mb-2" @click="downloadSample">
      Download Sample Excel
    </button>

    <!-- FILE UPLOAD -->
    <input type="file" accept=".xlsx,.xls" @change="handleFile" class="form-control mb-3" />

    <!-- SEARCH -->
    <input v-if="rows.length" class="form-control mb-2" placeholder="Search..." v-model="search" />

    <!-- PROGRESS BAR -->
    <div v-if="isImporting" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
          :style="{ width: progress + '%' }">
          {{ progress }}%
        </div>
      </div>
      <small class="text-muted">
        Imported {{ processedRows }} of {{ totalRows }} rows
      </small>
    </div>
    <!-- STATS -->

    <!-- TABLE -->
    <div class="table-responsive" style="max-height: 420px; overflow-y: auto;">
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>#</th>
            <th>Full Name</th>
            <th>ID</th>
            <th>Phone</th>
            <th>Gender</th>
            <th>DOB</th>
            <th>Member Number</th>
            <th>Remark</th>
            <!-- <th>Group</th> -->
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(r, i) in filteredRows" :key="i" :class="rowClass(r)">
            <td>{{ i + 1 }}</td>

            <td>
              <input class="form-control form-control-sm" v-model="r.fullName" @input="validateRow(r)" />
            </td>

            <td>
              <input class="form-control form-control-sm" v-model="r.idNumber" @input="validateRow(r)" />
            </td>

            <td>
              <input class="form-control form-control-sm" v-model="r.phoneNumber" @input="validateRow(r)" />
            </td>

            <td>
              <select class="form-select form-select-sm" v-model="r.gender" @change="validateRow(r)">
                <option value="">Select</option>
                <option>MALE</option>
                <option>FEMALE</option>
              </select>
            </td>

            <td>
              <input
  type="date"
  class="form-control form-control-sm"
  :value="r.dob"
  @input="r.dob = normalizeDate($event.target.value); validateRow(r)"
/>

            </td>
            <td>
              <input class="form-control form-control-sm" v-model="r.memberNumber" @input="validateRow(r)" />
            </td>

            <td>
              <span v-if="r._status === 'SUCCESS'" class="text-success">
                ✔ {{ r._remark }}
              </span>
              <span v-else-if="r._status === 'FAILED'" class="text-danger">
                ✖ {{ r._remark }}
              </span>
              <span v-else class="text-muted">
                Pending
              </span>
            </td>

            <td>
              <button class="btn btn-sm btn-danger" @click="deleteRow(i)">
                ✖
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- UNDO DELETE -->
    <button v-if="deletedRows.length" class="btn btn-warning mt-2" @click="undoDelete">
      Undo Delete
    </button>

    <!-- SUBMIT -->
    <button class="btn btn-success mt-3" @click="submitRows">
      Submit Valid Rows
    </button>

  </div>

</template>

<script>
import * as XLSX from 'xlsx'
import clientApi from '@/api/clientApi'

export default {
  data() {
    return {
      rows: [],
      deletedRows: [],
      search: '',

      // progress
      isImporting: false,
      progress: 0,
      processedRows: 0,
      totalRows: 0
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      return this.rows.filter(r =>
        Object.values(r).some(v =>
          String(v).toLowerCase().includes(this.search.toLowerCase())
        )
      )
    },

    duplicateCount() {
      return this.rows.filter(r => r._duplicate).length
    },

    invalidCount() {
      return this.rows.filter(r => Object.keys(r._errors).length).length
    },

    validCount() {
      return this.rows.length - this.invalidCount - this.duplicateCount
    }
  },

  methods: {
    normalizeDate(value) {
  if (!value) return ''

  // Excel serial date
  if (typeof value === 'number') {
    const utcDays = Math.floor(value - 25569)
    const date = new Date(utcDays * 86400 * 1000)
    return date.toISOString().slice(0, 10)
  }

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }

  // dd-mm-yyyy or dd/mm/yyyy
  const match = value.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
  if (match) {
    const [, dd, mm, yyyy] = match
    return `${yyyy}-${mm}-${dd}`
  }

  // Fallback
  const d = new Date(value)
  if (!isNaN(d)) {
    return d.toISOString().slice(0, 10)
  }

  return ''
},
    handleFile(e) {
      const reader = new FileReader()
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        const sheet = wb.Sheets[wb.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(sheet, { defval: '' })

        this.rows = data.map(r => ({
          fullName: r.fullName || '',
          idNumber: r.idNumber || '',
          phoneNumber: r.phoneNumber || '',
          dob: this.normalizeDate(r.dob),
          memberNumber: r.memberNumber || '',
          gender: r.gender || '',
          _errors: {},
          _duplicate: false,
          _status: 'PENDING', // PENDING | SUCCESS | FAILED
          _remark: ''
        }))


        this.rows.forEach(this.validateRow)
        this.detectDuplicates()
      }

      reader.readAsBinaryString(e.target.files[0])
    },

    validateRow(r) {
      r._errors = {}
      if (!r.fullName) r._errors.fullName = true
      if (!r.idNumber) r._errors.idNumber = true
      if (!r.phoneNumber) r._errors.phoneNumber = true
      this.detectDuplicates()
    },

    detectDuplicates() {
      const seenId = {}
      const seenPhone = {}

      this.rows.forEach(r => {
        r._duplicate = false

        if (
          (r.idNumber && seenId[r.idNumber]) ||
          (r.phoneNumber && seenPhone[r.phoneNumber])
        ) {
          r._duplicate = true
        }

        seenId[r.idNumber] = true
        seenPhone[r.phoneNumber] = true
      })
    },

    deleteRow(i) {
      this.deletedRows.push(this.rows[i])
      this.rows.splice(i, 1)
    },

    undoDelete() {
      this.rows.push(...this.deletedRows)
      this.deletedRows = []
    },

    rowClass(r) {
      if (r._status === 'SUCCESS') return 'table-success'
      if (r._status === 'FAILED') return 'table-danger'
      if (r._duplicate) return 'table-warning'
      if (Object.keys(r._errors).length) return 'table-danger'
      return ''
    },
    downloadSample() {
      const ws = XLSX.utils.json_to_sheet([{
        fullName: 'Jafari M. Akida',
        idNumber: '29319718',
        phoneNumber: '0759184063',
        gender: 'MALE',
        dob: '1992-05-23',
        memberNumber: 'CVL1'
      }])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Clients')
      XLSX.writeFile(wb, 'client_upload_sample.xlsx')
    },

    async submitRows() {
      const validRows = this.rows
        .map((r, index) => ({ r, index }))
        .filter(x =>
          !x.r._duplicate &&
          Object.keys(x.r._errors).length === 0
        )

      if (!validRows.length) {
        alert('No valid rows to submit')
        return
      }

      const CHUNK_SIZE = 10
      const chunks = []

      for (let i = 0; i < validRows.length; i += CHUNK_SIZE) {
        chunks.push(validRows.slice(i, i + CHUNK_SIZE))
      }

      this.isImporting = true
      this.progress = 0
      this.processedRows = 0
      this.totalRows = validRows.length

      for (const chunk of chunks) {
        const payload = chunk.map(x => ({
          index: x.index,
          fullName: x.r.fullName,
          idNumber: x.r.idNumber,
          phoneNumber: x.r.phoneNumber,
          dob: x.r.dob,
          memberNumber: x.r.memberNumber,
          gender: x.r.gender
        }))

        try {
          const res = await clientApi.importBatch(payload)

          res.data.forEach(result => {
            const row = this.rows[result.index]
            row._status = result.status
            row._remark = result.message
          })

        } catch (err) {
          // Mark entire chunk as failed
          chunk.forEach(x => {
            x.r._status = 'FAILED'
            x.r._remark = 'System error'
          })
        }

        this.processedRows += chunk.length
        this.progress = Math.round(
          (this.processedRows / this.totalRows) * 100
        )
      }

      this.isImporting = false
    }
  }
}

</script>